import { Stack, Typography } from '@mui/material'
import { CarrierVisitStatus, WorkQueueStatus } from '@operations/app/api'
import { WorkQueueStatusChip } from '@operations/components/chips/WorkQueueStatusChip'
import { convertOperationType } from '@operations/utils/helper'
import { useTranslate } from '@tolgee/react'
import { GeneralIcon, OptionsButton, useMinimalsTheme } from '@tom-ui/ui'
import { formatDateRange } from '@tom-ui/utils'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { canStartWorkQueue, canStopWorkQueue } from '../../helpers/work-instruction-table.helper'
import { CarrierVisitWorkQueueViewModel } from '../../models/work-instruction-view.model'

interface Props {
  isSelected: boolean
  workQueue: CarrierVisitWorkQueueViewModel
  visitStatus: CarrierVisitStatus
  onClick: () => void
  onStop: () => void
  onStart: () => void
  onRename: () => void
}
export const WorkQueueDetails = observer(
  ({ workQueue, isSelected, visitStatus, onClick, onStop, onStart, onRename }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    return (
      <Stack
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        gap={theme.customSpacing.m}
        padding={theme.customSpacing.s}
        onClick={onClick}
        sx={{
          borderLeft: isSelected
            ? `${theme.customSpacing.xxs} solid ${theme.palette.primary.main}`
            : 'none',
          cursor: 'pointer',
        }}
        data-cy={`work-queue-accordion${workQueue?.name?.toLowerCase().replace('', '-') ?? ''}`}
      >
        <Stack width='100%'>
          <Stack
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'
            gap={theme.customSpacing.m}
          >
            <Stack>
              {workQueue.name && <Typography variant='subtitle2'>{workQueue.name}</Typography>}
              <Typography variant='subtitle2'>{`${t(convertOperationType(workQueue.operationType))} 
                ${workQueue.bay < 0 ? t('unknownBay', 'Unknown bay') : t('bay', 'Bay') + ' ' + workQueue.bay} - ${t(_.lowerFirst(workQueue.vesselArea))}`}</Typography>
            </Stack>
            <Stack flexDirection='row' alignItems='center' gap={theme.customSpacing.xs}>
              <GeneralIcon color='secondary' />
              <Typography variant='caption' color='secondary'>
                X
              </Typography>
              <Typography variant='caption' color='secondary'>
                {workQueue.workInstructionsAmount}
              </Typography>
            </Stack>
          </Stack>
          <Typography variant='caption' color='secondary'>
            {formatDateRange(workQueue.startTime, workQueue.endTime)}
          </Typography>

          {workQueue.status && <WorkQueueStatusChip status={workQueue.status} />}
        </Stack>

        <OptionsButton
          tooltip={t('moreOptions', 'More options')}
          position='right'
          testId='work-queue-more-options-button'
          options={[
            {
              label: t('showAll', 'Show All'),
              onClick: () => onClick(),
            },
            {
              label: t('stop', 'Stop'),
              onClick: () => onStop(),
              disabled: !canStopWorkQueue(workQueue.status, visitStatus),
              testId: 'stop-work-queue',
            },
            {
              label:
                workQueue.status === WorkQueueStatus.Stopped
                  ? t('resume', 'Resume')
                  : t('start', 'Start'),
              onClick: () => onStart(),
              disabled: !canStartWorkQueue(workQueue.status, visitStatus),
              testId: 'start-work-queue',
            },
            {
              label: workQueue.name ? t('renameWQ', 'Rename WQ') : t('addWQName', 'Add WQ name'),
              onClick: () => onRename(),
            },
          ]}
        />
      </Stack>
    )
  },
)
